import { useRuntimeConfig } from '#app'
import { CraftUtil } from '../util/craft-util'

/**
 * Composable for making requests to the Craft CMS API.
 *
 * Architecture:
 * - SSR: Fetches directly from the CMS using credentials from CloudFront headers (no extra hop).
 * - CSR: Fetches via same-origin Nuxt server proxy routes (/api/graphql, /api/cms/*),
 *        which inject the bearer token server-side. The token never reaches the browser.
 *
 * CMS URLs (adminUrl, craftUrl) are transferred from SSR → CSR via useState (no secrets).
 * CSRF protection: A per-render token (double-submit cookie pattern) guards proxy routes.
 */
export function useGraphQL() {
    const runtimeConfig = useRuntimeConfig()
    const nuxtApp = useNuxtApp()

    // CSRF token for proxy requests (generated by csrf.server.ts plugin, validated by csrf middleware)
    const csrfToken = useState<string>('csrf-token')

    // SSR-only: full config with token for direct CMS access
    let serverConfig: CraftCmsConfig | null = null

    if (import.meta.server) {
        const headers = nuxtApp.ssrContext.event.node.req.headers
        serverConfig = CraftUtil.getServerAuthConfig(runtimeConfig, headers)

        if (!serverConfig.endpoint || !serverConfig.token) {
            throw createError({
                statusCode: 500,
                statusMessage: 'GraphQL endpoint or token missing.',
            })
        }
    }

    // Transfer non-secret CMS URLs from SSR → CSR via useState (replaces the cookie approach)
    const adminUrl = useState<string>('cms-admin-url', () => {
        return serverConfig ? replace(serverConfig.endpoint, '/api', '/admin') : ''
    })
    const craftUrl = useState<string>('cms-craft-url', () => {
        return serverConfig ? replace(serverConfig.endpoint, '/api', '') : ''
    })
    const cmsApiUrl = useState<string>('cms-api-url', () => {
        return serverConfig?.endpoint || ''
    })

    const query = async ({ query, variables = {}, ...options }) => {
        const { isPreview, previewToken } = usePreview()

        const beforeFetchStamp = Date.now() / 1000

        // SSR: fetch directly from CMS (credentials available from headers, no proxy hop)
        if (import.meta.server && serverConfig) {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${serverConfig.token}`,
            }

            if (isPreview.value) {
                headers['X-Craft-Token'] = previewToken.value
            }

            const queryOptions = {
                key: options?.key,
                method: 'POST' as const,
                headers,
                body: JSON.stringify({ query, variables }),
            }

            // if server = false, skip useFetch on SSR (will run client-side only)
            if (options.server === false) {
                return null
            }

            const { data, error } = await useFetch(serverConfig.endpoint, queryOptions)

            if (error.value) {
                throw createError({
                    statusCode: 500,
                    statusMessage: 'Fetch error',
                })
            }

            if (data.value?.errors) {
                console.error('GraphQL Error:', data.value?.errors?.[0].message)
            }

            console.groupCollapsed(`${options.key} query in`, `${(Date.now() / 1000 - beforeFetchStamp).toFixed(2)}s`)
            console.groupEnd()

            return data.value?.data
        }

        // CSR: fetch via same-origin server proxy (token stays server-side)
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken.value,
        }

        if (isPreview.value) {
            headers['X-Craft-Token'] = previewToken.value
        }

        const proxyOptions = {
            key: options?.key,
            method: 'POST' as const,
            headers,
            body: JSON.stringify({ query, variables }),
        }

        // if server = false, use $fetch (client-only, no SSR hydration)
        if (options.server === false) {
            try {
                const response = await $fetch('/api/graphql', proxyOptions)

                if (response?.errors) {
                    console.error('GraphQL Error:', response?.errors?.[0].message)
                }

                return response.data
            }
            catch {
                throw createError({
                    statusCode: 500,
                    statusMessage: 'Something went wrong',
                })
            }
        }

        const { data, error } = await useFetch('/api/graphql', proxyOptions)

        if (error.value) {
            throw createError({
                statusCode: 500,
                statusMessage: 'Fetch error',
            })
        }

        if (data.value?.errors) {
            console.error('GraphQL Error:', data.value?.errors?.[0].message)
        }

        console.groupCollapsed(`${options.key} query in`, `${(Date.now() / 1000 - beforeFetchStamp).toFixed(2)}s`)
        if (import.meta.client) {
            console.log(query)
            console.log(variables)
        }
        console.groupEnd()

        return data.value?.data
    }

    const cmsQuery = async ({ uri, variables = {}, ...options }) => {
        const { isPreview, previewToken, previewTimestamp } = usePreview()

        if (isPreview.value) {
            // Needed for cache busting in preview mode
            const separator = uri.includes('?') ? '&' : '?'
            uri += `${separator}previewTimestamp=${previewTimestamp.value}`
        } else {
            const route = useRoute()

            if (route.query && 'no-cache' in route.query) {
                const separator = uri.includes('?') ? '&' : '?'
                uri += `${separator}no-cache=${Date.now()}`
            }
        }

        const beforeFetchStamp = Date.now() / 1000

        // SSR: fetch directly from CMS data-api (credentials available from headers)
        if (import.meta.server && serverConfig) {
            const dataApiEndpoint = replace(serverConfig.endpoint, '/api', '/data-api')

            const headers: Record<string, string> = {
                'Authorization': `Bearer ${serverConfig.token}`,
                'Cache-Control': 'public',
            }

            if (isPreview.value) {
                headers['X-Craft-Token'] = previewToken.value
            }

            if (options.server === false) {
                return { data: null }
            }

            const { data, error } = await useFetch(`${dataApiEndpoint}/${uri}`, {
                ...options,
                headers,
                query: variables,
            })

            if (error.value) {
                throw createError({
                    statusCode: 500,
                    statusMessage: 'Something went wrong',
                    data: { message: error.value },
                })
            }

            if (data.value?.errors) {
                console.error('CMS Request Error:', data.value?.errors?.[0].message)
            }

            console.groupCollapsed(`${options.key} query in`, `${(Date.now() / 1000 - beforeFetchStamp).toFixed(2)}s`)
            console.groupEnd()

            return { data }
        }

        // CSR: fetch via same-origin server proxy (token stays server-side)
        const headers: Record<string, string> = {
            'Cache-Control': 'public',
            'X-CSRF-Token': csrfToken.value,
        }

        if (isPreview.value) {
            headers['X-Craft-Token'] = previewToken.value
        }

        // if server = false, use $fetch (client-only)
        if (options.server === false) {
            try {
                const response = await $fetch(`/api/cms/${uri}`, {
                    ...options,
                    headers,
                    query: variables,
                })

                if (response?.errors) {
                    console.error('CMS Data Request Error:', response?.errors?.[0].message)
                }

                return { data: response.data }
            }
            catch {
                throw createError({
                    statusCode: 500,
                    statusMessage: 'Something went wrong',
                })
            }
        }

        const { data, error } = await useFetch(`/api/cms/${uri}`, {
            ...options,
            headers,
            query: variables,
        })

        if (error.value) {
            throw createError({
                statusCode: 500,
                statusMessage: 'Something went wrong',
                data: { message: error.value },
            })
        }

        if (data.value?.errors) {
            console.error('CMS Request Error:', data.value?.errors?.[0].message)
        }

        console.groupCollapsed(`${options.key} query in`, `${(Date.now() / 1000 - beforeFetchStamp).toFixed(2)}s`)
        if (import.meta.client) {
            console.log(query)
            console.log(variables)
        }
        console.groupEnd()

        return { data }
    }

    return {
        apiUrl: cmsApiUrl.value,
        adminUrl: adminUrl.value,
        craftUrl: craftUrl.value,
        query,
        cmsQuery
    }
}
